import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    Button,
    Box,
    Typography,
    Alert,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import {
    ocrCustomerInfo,
    createCustomer,
} from '../services/api';
import { getQueryParam } from '../utils/helpers';
import AppFooter from '../components/shared/AppFooter';
import StepUploadDocuments from '../components/registration/StepUploadDocuments';
import StepVerifyInfo from '../components/registration/StepVerifyInfo';
import StepSuccess from '../components/registration/StepSuccess';

const steps = ['Giấy Tờ', 'Thông Tin', 'Hoàn Tất'];

const RegistrationPage = () => {

    const [activeStep, setActiveStep] = useState(0);
    const [qrToken, setQrToken] = useState('');
    const [ignoreNameMatch, setIgnoreNameMatch] = useState(false);

    // Step 0 state
    const [images, setImages] = useState([]);
    const [ocrResponse, setOcrResponse] = useState(null);
    const [step1Loading, setStep1Loading] = useState(false);
    const [step1Error, setStep1Error] = useState('');
    const [nameWarning, setNameWarning] = useState('');

    // Step 1 state
    const [formData, setFormData] = useState({
        fullName: '',
        idNumber: '',
        address: '',
        phoneNumber: '',
        email: '',
        employeeCode: '',
        dateOfBirth: '',
        gender: '',
    });
    const [step2Loading, setStep2Loading] = useState(false);
    const [step2Error, setStep2Error] = useState('');

    // Step 2 state
    const [customerData, setCustomerData] = useState(null);

    // Get QR token from URL on mount
    useEffect(() => {
        const token = getQueryParam('token');
        const ignoreMatch = getQueryParam('ignore-match-fullname');

        // Default token for testing/fallback
        const DEFAULT_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmb3JtVXJsIjoiaHR0cHM6Ly9sb3lhbHR5LmRhaW1pbmhwaGF0LmNvbS52bi9wcmlvcml0eS1yZWdpc3RlciIsIm9yZ0lkIjoiNjc4MmEwYzk1OTczM2VjNTZiZGMwYmIwIiwiaWF0IjoxNzczOTAwNTU1LCJleHAiOjE3ODE2NzY1NTV9.2-DIPZGA7IhVvXZnT-BxswX7UpqJH8rcf4FVDJ0wEiQ';

        if (token && token.trim() !== '') {
            setQrToken(token);
        } else {
            setQrToken(DEFAULT_TOKEN);
        }

        if (ignoreMatch === 'true') {
            setIgnoreNameMatch(true);
        }
    }, []);

    // Helper: Normalize name for comparison
    const normalizeName = (name) => {
        return name
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    };

    // Helper: Check if names match across documents
    const checkNameConsistency = (documents) => {
        const names = [];

        documents.forEach((doc) => {
            if (doc.fields?.fullName?.value) {
                names.push(normalizeName(doc.fields.fullName.value));
            }
        });

        if (names.length < 2) return true;

        const firstName = names[0];
        return names.every((name) => name === firstName);
    };

    // Step 0: OCR
    const handleOCR = async () => {
        setStep1Loading(true);
        setStep1Error('');
        setNameWarning('');

        try {
            const imageBase64Array = images.map((img) => img.base64);
            const data = await ocrCustomerInfo(imageBase64Array);

            setOcrResponse(data);

            if (!ignoreNameMatch && data.documents && data.documents.length > 1) {
                const namesMatch = checkNameConsistency(data.documents);
                if (!namesMatch) {
                    setNameWarning(
                        'Cảnh báo: Họ tên trên các giấy tờ không khớp nhau. Vui lòng kiểm tra lại.'
                    );
                }
            }

            setFormData({
                fullName: data.fullName || '',
                idNumber: data.idNumber || '',
                address: data.address || '',
                phoneNumber: data.phoneNumber || '',
                email: data.email || '',
                employeeCode: data.employeeCode || '',
                dateOfBirth: data.dateOfBirth || '',
                gender: data.gender || '',
            });

            setActiveStep(1);
        } catch (err) {
            setStep1Error(err.message);
        } finally {
            setStep1Loading(false);
        }
    };

    // Step 1: Submit Registration
    const handleSubmit = async () => {
        if (!ignoreNameMatch && nameWarning) {
            setStep2Error('Không thể tiếp tục do họ tên trên các giấy tờ không khớp nhau.');
            return;
        }

        setStep2Loading(true);
        setStep2Error('');

        try {
            // Map images by document type from OCR response
            const imagesObj = {};

            if (ocrResponse?.documents) {
                ocrResponse.documents.forEach((doc, index) => {
                    if (doc.type === 'cccd' && images[index]) {
                        imagesObj.cccd = images[index].base64;
                    } else if (doc.type === 'vneid' && images[index]) {
                        imagesObj.vneid = images[index].base64;
                    } else if (doc.type === 'employee_card' && images[index]) {
                        imagesObj.employeeCard = images[index].base64;
                    }
                });
            }

            // Fallback: if no documents matched, use first two images
            if (!imagesObj.cccd && !imagesObj.vneid && images[0]) {
                imagesObj.cccd = images[0].base64;
            }
            if (!imagesObj.employeeCard && images[1]) {
                imagesObj.employeeCard = images[1].base64;
            }

            const payload = {
                ...formData,
                qrToken: qrToken,
                images: imagesObj,
            };

            const data = await createCustomer(payload);
            setCustomerData(data.customer);
            setActiveStep(2);
        } catch (err) {
            setStep2Error(err.message);
        } finally {
            setStep2Loading(false);
        }
    };

    // Handle back button
    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    // Reset form
    const handleReset = () => {
        setActiveStep(0);
        setImages([]);
        setOcrResponse(null);
        setNameWarning('');
        setFormData({
            fullName: '',
            idNumber: '',
            address: '',
            phoneNumber: '',
            email: '',
            employeeCode: '',
            dateOfBirth: '',
            gender: '',
        });
        setCustomerData(null);
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <StepUploadDocuments
                        images={images}
                        setImages={setImages}
                        onOCR={handleOCR}
                        loading={step1Loading}
                        error={step1Error}
                    />
                );
            case 1:
                return (
                    <Box>
                        {nameWarning && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                {nameWarning}
                            </Alert>
                        )}
                        <StepVerifyInfo
                            formData={formData}
                            setFormData={setFormData}
                            onSubmit={handleSubmit}
                            loading={step2Loading}
                            error={step2Error}
                        />
                    </Box>
                );
            case 2:
                return <StepSuccess customerData={customerData} onClose={handleReset} />;
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
            }}
        >
            <Container maxWidth="md" sx={{ flex: 1, py: 4 }}>
                <Card elevation={3}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h5" align="center" color="primary" sx={{ mb: 1 }}>
                            Đăng Ký Khách Hàng Ưu Đãi
                        </Typography>

                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                            Hoàn thành các bước để trở thành khách hàng ưu đãi
                        </Typography>

                        <Stepper
                            activeStep={activeStep}
                            orientation="horizontal"
                            sx={{ mb: 2 }}
                        >
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        <Box>{getStepContent(activeStep)}</Box>

                        {activeStep === 1 && !step2Loading && (
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    startIcon={<BackIcon />}
                                    onClick={handleBack}
                                >
                                    Quay lại
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>

            <AppFooter />
        </Box>
    );
};

export default RegistrationPage;
